import { DEV, EDITOR } from 'cc/env';
import { _decorator, v3, Vec3, Node, CCInteger, Vec2, v2 } from 'cc';
import OrientationManager from '../../../OrientationManager';
import { convertCenterPointToWorldSpace } from '../../Utils/Utility';
import OrientedComponent, { OrientedBaseProperty } from './OrientedComponent';

const { ccclass, property, menu } = _decorator;

@ccclass('OrientedNodeProperties')
class OrientedNodeProperties extends OrientedBaseProperty {
    @property({ tooltip: DEV && '旋轉為該方向時，是否該依據設定調整顯示狀態', group: 'Node' })
    public updateActive: boolean = false;
    @property({
        tooltip: DEV && '是否顯示出來',
        visible(this: OrientedNodeProperties) {
            return this.updateActive;
        },
        group: 'Node',
    })
    public active: boolean = false;

    @property({ tooltip: DEV && '旋轉為該方向時，是否該依據設定調整顯示狀態', group: 'UIOpacity' })
    public updateOpacity: boolean = false;
    @property({
        tooltip: DEV && '是否調整透明度',
        visible(this: OrientedNodeProperties) {
            return this.updateOpacity;
        },
        group: 'UIOpacity',
    })
    public opacity: number = 255;

    @property({ tooltip: DEV && '旋轉為該方向時，是否該依據設定調整角度', group: 'Node' })
    public updateAngle: boolean = false;
    @property({
        type: CCInteger,
        tooltip: DEV && '旋轉的角度',
        visible(this: OrientedNodeProperties) {
            return this.updateAngle;
        },
        group: 'Node',
    })
    public angle: number = 0;
    @property({ tooltip: DEV && '旋轉為該方向時，是否該依據設定調整縮放比例', group: 'Node' })
    public updateScale: boolean = false;
    @property({
        tooltip: DEV && '要縮放的比例',
        visible(this: OrientedNodeProperties) {
            return this.updateScale;
        },
        group: 'Node',
    })
    public scale: Vec3 = v3();
    @property({ tooltip: DEV && '旋轉為該方向時，是否該依據設定調整父節點', group: 'Node' })
    public updateParent: boolean = false;
    @property({
        type: Node,
        tooltip: DEV && '調整父節點',
        visible(this: OrientedNodeProperties) {
            return this.updateParent;
        },
        group: 'Node',
    })
    public parentNode: Node = null;
    @property({
        tooltip: DEV && '是否在修改TRS前，先設定父節點，若是要依據父節點的座標系統進行TRS調整，請勾選此項',
        visible(this: OrientedNodeProperties) {
            return this.updateParent;
        },
        group: 'Node',
    })
    public updateParentFirst: boolean = false;

    @property({ tooltip: DEV && '旋轉為該方向時，是否該依據設定調整座標', group: 'Position' })
    public updatePosition: boolean = false;
    @property({
        tooltip: DEV && '調整座標數值是否以世界座標為基準',
        visible(this: OrientedNodeProperties) {
            return this.updatePosition;
        },
        group: 'Position',
    })
    public isWorldPosition: boolean = false;
    @property({
        tooltip: DEV && '座標，若是 isWorldPosition 打勾的話，將以中心(Canvas)為原點；否則將直接依據當前節點的座標系統直接設定',
        visible(this: OrientedNodeProperties) {
            return this.updatePosition;
        },
        group: 'Position',
    })
    public position: Vec3 = v3();
    @property({
        tooltip: DEV && '座標，是否只設置 x 座標',
        visible(this: OrientedNodeProperties) {
            return this.updatePosition && !this.updatePositionYOnly;
        },
        group: 'Position',
    })
    public updatePositionXOnly = false;
    @property({
        tooltip: DEV && '座標，是否只設置 y 座標',
        visible(this: OrientedNodeProperties) {
            return this.updatePosition && !this.updatePositionXOnly;
        },
        group: 'Position',
    })
    public updatePositionYOnly = false;

    @property({ tooltip: DEV && '旋轉為該方向時，是否該依據設定調整錨點(Anchor Point)，節點上必需有UITransform組件才會生效', group: 'UITransform' })
    public updateAnchor: boolean = false;
    @property({
        tooltip: DEV && '錨點設定(需有UITransform組件才會生效)',
        visible(this: OrientedNodeProperties) {
            return this.updateAnchor;
        },
        group: 'UITransform',
    })
    public anchor: Vec2 = v2();

    @property({ tooltip: DEV && '旋轉為該方向時，是否該依據設定調整寬高數值', group: 'UITransform' })
    public updateUITransformSize: boolean = false;
    @property({
        tooltip: DEV && '寬高數值(需有UITransform組件才會生效)',
        visible(this: OrientedNodeProperties) {
            return this.updateUITransformSize;
        },
        group: 'UITransform',
    })
    public uITransformSize: Vec2 = v2();
    @property({
        tooltip: DEV && '寬高，是否只設置寬',
        visible(this: OrientedNodeProperties) {
            return this.updateUITransformSize && !this.updateUITransformSizeHeightOnly;
        },
        group: 'UITransform',
    })
    public updateUITransformSizeWidthOnly = false;
    @property({
        tooltip: DEV && '寬高，是否只設置高',
        visible(this: OrientedNodeProperties) {
            return this.updateUITransformSize && !this.updateUITransformSizeWidthOnly;
        },
        group: 'UITransform',
    })
    public updateUITransformSizeHeightOnly = false;
}

@ccclass('OrientedNode')
@menu('Orientation/OrientedNode')
export default class OrientedNode extends OrientedComponent<Node> {
    @property({ type: OrientedNodeProperties, group: '橫式' })
    public readonly landscapeProperties: OrientedNodeProperties = new OrientedNodeProperties();
    @property({ type: OrientedNodeProperties, group: '直式' })
    public readonly portraitProperties: OrientedNodeProperties = new OrientedNodeProperties();

    constructor(name?: string) {
        super(name);
        if (!EDITOR) {
            // eslint-disable-next-line no-constructor-return
            return new Proxy(this, {
                set: (target, p, value) => {
                    // to ensure active is true to register event listener correctly
                    if (value && (p === 'landscapeProperties' || p === 'portraitProperties')) {
                        const { updateActive } = value as OrientedNodeProperties;
                        if (updateActive && target.node) {
                            target.node.active = true;
                        }
                    } else if (p === 'node') {
                        const node = value as Node;
                        let updateActive = false;
                        if (target.landscapeProperties?.updateActive || target.portraitProperties?.updateActive) {
                            updateActive = true;
                        }
                        if (node && updateActive) {
                            node.active = true;
                        }
                    }
                    return Reflect.set(target, p, value);
                },
            });
        }
    }

    public onLoad(): void {
        this.game.orientation.on(OrientationManager.EVENT_TYPE.ORIENTATION_CHANGE, this.onOrientationChanged, this);
        this.onOrientationChanged(this.game.orientation.current);
    }

    // 監聽放在這邊若有控制到 active 的話, onDisable後會直接回不來, 所以改到 onLoad 進行監聽
    public onEnable(): void {}

    public onDisable(): void {}

    protected updateOrientationProperties(properties: OrientedNodeProperties) {
        if (properties.updateParentFirst) {
            this._updateParent(properties);
        }
        if (properties.updatePosition) {
            const nodePosition = properties.isWorldPosition
                ? this.target.parent.transform.convertToNodeSpaceAR(convertCenterPointToWorldSpace(properties.position))
                : properties.position;
            if (properties.updatePositionXOnly) {
                this.target.x = nodePosition.x;
            } else if (properties.updatePositionYOnly) {
                this.target.y = nodePosition.y;
            } else {
                this.target.setPosition(nodePosition);
            }
        }
        if (properties.updateAngle) {
            this.target.angle = properties.angle;
        }
        if (properties.updateScale) {
            this.target.setScale(properties.scale);
        }
        if (properties.updateActive) {
            this.target.active = properties.active;
        }
        if (properties.updateOpacity) {
            this.target.opacity = properties.opacity;
        }
        if (!properties.updateParentFirst) {
            this._updateParent(properties);
        }

        const { transform } = this.target;
        if (properties.updateAnchor) {
            if (transform) {
                transform.anchorPoint = properties.anchor;
            }
        }
        if (properties.updateUITransformSize) {
            if (transform) {
                if (properties.updateUITransformSizeWidthOnly) {
                    transform.width = properties.uITransformSize.x;
                } else if (properties.updateUITransformSizeHeightOnly) {
                    transform.height = properties.uITransformSize.y;
                } else {
                    transform.width = properties.uITransformSize.x;
                    transform.height = properties.uITransformSize.y;
                }
            }
        }
    }

    protected getTarget() {
        return this.node;
    }

    private _updateParent(properties: OrientedNodeProperties) {
        if (properties.updateParent) {
            this.target.setParent(properties.parentNode);
        }
    }
}
